import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { View } from 'react-native';
import type { Loan } from 'app/types/loan';
import { useCompleteOneLoan, useOneLoan } from 'app/network/loan';
import React, { useEffect, useState } from 'react';
import { Modal } from 'app/design/primitives/modal';
import { useTranslation } from 'react-i18next';
import { useUser } from 'app/network/user';
import { ListingSubtype, ListingType, TransactionMode } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';

export interface LoanReturnConfirmationProps {
  loan: Loan;
  setLoan: React.Dispatch<React.SetStateAction<Loan>>;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  showCreateOfferModal: () => void;
  onCompleted?: (loan: Loan) => void;
}

export function useLoanReturnConfirmation(
  originalLoan: Loan,
  showCreateOfferModal: () => void,
  onCompleted?: (loan: Loan) => void,
): LoanReturnConfirmationProps {
  const [loan, setLoan] = useState(originalLoan);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setLoan(originalLoan), 0);
    return () => clearTimeout(timeout);
  }, [originalLoan]);

  return {
    loan,
    setLoan,
    isOpen,
    setIsOpen,
    showCreateOfferModal,
    onCompleted,
  };
}

export function LoanReturnConfirmation({
  loan,
  setLoan,
  isOpen,
  setIsOpen,
  showCreateOfferModal,
  onCompleted,
}: LoanReturnConfirmationProps) {
  const { t } = useTranslation('one-listing');

  const completeLoan = useCompleteOneLoan();

  // Reload loan info when it gets updated
  const { refetch: refetchLoan } = useOneLoan(loan.id, {
    enabled: false,
  });

  const { data: user } = useUser();
  const isLender = loan.lenderId === user?.id;
  const otherUser = isLender ? loan.borrower : loan.lender;
  const isService = loan.listing.subtype === ListingSubtype.service;
  const isDonation = isDonationListing(loan.listing);
  const isSell = loan.listing.transactionMode === TransactionMode.sell;

  const confirmReturnPress = () => {
    completeLoan.mutate(loan.id, {
      onSuccess: (completedLoan) => {
        setLoan(completedLoan);
        void refetchLoan().then(({ data: refreshedLoan }) => {
          if (refreshedLoan) setLoan(refreshedLoan);
        });
        setIsOpen(false);
        if (
          loan.listing.type === ListingType.request &&
          loan.listing.subtype === ListingSubtype.object &&
          !isDonation &&
          isLender
        ) {
          showCreateOfferModal();
        }
        onCompleted?.(completedLoan);
      },
    });
  };

  const tPrefix = isSell
    ? 'confirmSaleModal'
    : isService
      ? 'confirmServiceDoneModal'
      : isDonation
        ? 'confirmDonationDoneModal'
        : 'confirmReturnModal';
  const roleSuffix = isSell ? `.${isLender ? 'lender' : 'borrower'}` : '';

  return (
    <Modal onClose={() => setIsOpen(false)} open={isOpen} transparent>
      <Modal.Card>
        <Modal.Title>{t(`${tPrefix}.title${roleSuffix}`)}</Modal.Title>

        <Column>
          <Text textAlign="center">
            {t(`${tPrefix}.text.${isLender ? 'lender' : 'borrower'}`, { name: otherUser.profile!.firstName })}
          </Text>
        </Column>

        <View>
          <Button
            loading={completeLoan.isPending}
            marginBottom="spacing.general.x4"
            onPress={confirmReturnPress}
            variant="primary"
          >
            {t(`${tPrefix}.primaryButtonText${roleSuffix}`)}
          </Button>

          <Button onPress={() => setIsOpen(false)} variant="secondary">
            {t(`${tPrefix}.secondaryButtonText`)}
          </Button>
        </View>
      </Modal.Card>
    </Modal>
  );
}
